import React, { createContext, useContext, useRef } from 'react';
import { useStore } from 'zustand';
import { createAnytimeTicketStore, AnytimeTicketStore } from './createAnytimeTicketStore';

type AnytimeTicketStoreApi = ReturnType<typeof createAnytimeTicketStore>;

const AnytimeTicketContext = createContext<AnytimeTicketStoreApi | undefined>(undefined);

interface Props {
	children: React.ReactNode;
}

export default function AnytimeTicketProvider({ children }: Props) {
	// Initialize the store but never re-create it unless the provider unmounts
	const storeRef = useRef<AnytimeTicketStoreApi>();
	if (!storeRef.current) {
		storeRef.current = createAnytimeTicketStore();
	}

	return <AnytimeTicketContext.Provider value={storeRef.current}>{children}</AnytimeTicketContext.Provider>;
}

export function useAnytimeTicketStore<T>(
	selector: (state: AnytimeTicketStore) => T,
	equalityFn?: (left: T, right: T) => boolean,
): T {
	const storeApi = useContext(AnytimeTicketContext);
	if (!storeApi) {
		throw new Error('useAnytimeTicketStore must be used within AnytimeTicketProvider');
	}
	return useStore(storeApi, selector, equalityFn);
}

export function useAnytimeTicketStoreApi(): AnytimeTicketStoreApi {
	const storeApi = useContext(AnytimeTicketContext);
	if (!storeApi) {
		throw new Error('useAnytimeTicketStoreApi must be used within AnytimeTicketProvider');
	}
	return storeApi;
}
